import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BackgroundImage, Image, withContent } from '@bubbletea/core';
import { pollWindowVariable } from '@mooncake/mooncake-gateway-ecom/utils';
import Title from '../../UI/Title';
import { IContent } from '../../../types';
import { Content, ContentWrapper, InfoSection, OverTitle, TrailerButton, TrailerContainer } from './styles';
import { CirclePlayIcon, TripleSlashIcon } from '../../UI/Icons';
import Loader from '../../../core/UI/Loader';
import sendEvent from '../../../utils/sendEvent';
import { IStoreState } from '../../../types/store';

interface IAboutProps {
  context: IContent;
}

const About = ({ context }: IAboutProps) => {
  const [impressionEventRegistered, setImpressionEventRegistered] = useState(false);
  const contentSelector = useSelector((state: IStoreState) => ({
    analyticsLoaded: state.app.analyticsLoaded,
  }));
  const { analyticsLoaded } = contentSelector;

  useEffect(() => {
    const impressionEventDetails = {
      location: 'what is rocksmith plus',
      category: 'scroll',
      locationPosition: process.env['REACT_APP_PAGE_TYPE'] === 'subscriber' ? 6 : 3,
      action: 'visible',
    };
    if (analyticsLoaded && impressionEventRegistered) {
      setImpressionEventRegistered(false);
      sendEvent(impressionEventDetails, 'impressionevent');
    }
  }, [impressionEventRegistered, analyticsLoaded]);

  const [watchVideo, setWatchVideo] = useState(false);
  const { googlesheet } = context;
  const { about } = googlesheet;
  const { overTitle, title, description, aboutButton } = about;

  const youtubeRegex = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
  const youtubeMatches = about.aboutButton.youtubeLink?.match(youtubeRegex);
  const youtubeCode = youtubeMatches ? youtubeMatches[1] : null;

  const verifyVideoCookie = useCallback(async () => {
    const videoConsent = await pollWindowVariable('ConsentResources.videoConsent');
    if (videoConsent) {
      const eventDetails = {
        location: 'what is rocksmith plus',
        category: 'video',
        locationPosition: process.env['REACT_APP_PAGE_TYPE'] === 'subscriber' ? 6 : 3,
        action: 'rocksmith plus : watch trailer',
      };
      sendEvent(eventDetails);
      setWatchVideo(true);
    }
  }, []);

  return aboutButton && youtubeCode ? (
    <ContentWrapper
      id={'about'}
      whileInView={'sendevent'}
      onAnimationStart={() => setImpressionEventRegistered(true)}
      viewport={{ once: true }}
    >
      <BackgroundImage imageNames={['about_bg.png']} classNames={['about-bg']} dynamic>
        <Content className="section-wrapper">
          {!watchVideo ? (
            <TrailerButton
              initial={{
                opacity: 0,
                y: 3,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                type: 'spring',
              }}
              viewport={{ once: true }}
              onClick={() => verifyVideoCookie()}
              className={'youtube-content'}
            >
              <CirclePlayIcon />
              <Image imageNames={['trailer.jpg']} classNames={['player-image']} altText="Player" dynamic />
            </TrailerButton>
          ) : (
            <TrailerContainer>
              <iframe
                src={`https://www.youtube.com/embed/${youtubeCode}?autoplay=1`}
                title={about.aboutButton.youtubeTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              <Loader backgroundColor={'transparent'} />
            </TrailerContainer>
          )}
          <InfoSection>
            <OverTitle>
              <TripleSlashIcon />
              {overTitle}
            </OverTitle>
            <Title uppercase>{title}</Title>
            <p>{description}</p>
          </InfoSection>
        </Content>
      </BackgroundImage>
    </ContentWrapper>
  ) : null;
};

export default withContent(About);
